/**
 * Corrections round — pure logic for the doctor's corrections flow (both designs).
 *
 * Contract: backend docs/corrections-api.md (branch hardening/corrections, migration 67).
 *   GET  /clinic/doctor/{type}/{id}/corrections                  → { request_status, round | null }
 *   PUT  /clinic/doctor/{type}/{id}/corrections/items/{item_id}  → save one answer (no resubmit) → round
 *   POST /clinic/doctor/{type}/{id}/corrections/resubmit         → every item answered; 409 on a stale round
 *   Documents are answered by uploading the flagged type to the item's upload_endpoint.
 *
 * Rules kept here (unit-tested in corrections.test.ts):
 * - Progress counts only what the server confirmed (`addressed`), never local edits.
 * - Only flagged items are ever sent; document items are never PUT.
 * - A local draft keeps typed values per doctor + request, restricted to the items of the current round.
 * - Deadlines come only from the API's deadline object (no hard-coded day counts).
 */
import type { ApiError } from "./api";
import type { Lang } from "./i18n";

export type CorrTarget = "doctor" | "request" | "clinic";
export type CorrKind = "field" | "document" | "location";
export type DeadlineState = "active" | "warning" | "overdue" | "exhausted" | "not_applicable";

export interface DeadlineInfo {
  requested_at?: string | null;
  applies?: boolean;
  warning_at?: string | null;
  auto_reject_at?: string | null;
  days_remaining?: number | null;
  state?: DeadlineState | string | null;
  policy?: Record<string, any> | null;
}

export interface RoundAttachment {
  id: string;
  file_name?: string | null;
  url?: string | null;
  created_at?: string | null;
}

export interface RoundItemCurrent {
  value?: any;
  display?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  attachments?: RoundAttachment[];
}

export interface RoundItem {
  id: string;
  target: CorrTarget | string;
  kind: CorrKind | string;
  field_name: string;
  document_type?: string | null;
  field_type?: string | null;
  label?: string | null;
  reason?: string | null;
  doctor_writable?: boolean;
  current?: RoundItemCurrent | null;
  response_status?: "pending" | "saved" | "resubmitted" | string;
  saved_value?: { value?: any; latitude?: number | null; longitude?: number | null } | null;
  saved_at?: string | null;
  addressed?: boolean;
  upload_endpoint?: string | null;
  new_uploads?: string[];
  /** Documents only: the files staff flagged (still listed after the doctor replaced them). */
  original?: { attachments?: RoundAttachment[] | null } | null;
}

export interface DoctorRound {
  round_id: string;
  version: number;
  number?: number;
  status?: string;
  request_id: string;
  request_type: string;
  request_number?: string | null;
  general_notes?: string | null;
  requested_at?: string | null;
  deadline?: DeadlineInfo | null;
  after_resubmit?: { status?: string; message_ar?: string; message_en?: string } | null;
  items: RoundItem[];
  groups?: Array<{ target: string; item_ids: string[] }>;
  counts?: { total: number; addressed: number; remaining: number };
  can_resubmit?: boolean;
  missing_item_ids?: string[];
}

export interface CorrectionsPayload {
  request_status: string;
  round: DoctorRound | null;
}

/** A location answer as the editors hold it (strings, like GeoValue). */
export interface GeoAnswer {
  lat: string;
  lng: string;
  address?: string;
}

export type LocalValue = string | number | GeoAnswer | null;

// ---------------------------------------------------------------- endpoints

export const correctionsPath = (type: string, requestId: string) => `/clinic/doctor/${type}/${requestId}/corrections`;
export const saveItemPath = (type: string, requestId: string, itemId: string) => `${correctionsPath(type, requestId)}/items/${itemId}`;
export const resubmitPath = (type: string, requestId: string) => `${correctionsPath(type, requestId)}/resubmit`;

/** Where a document item's file goes (server-provided; the fallback mirrors the server rule). */
export function uploadPathFor(item: RoundItem, type: string, requestId: string): string {
  if (item.upload_endpoint) return item.upload_endpoint;
  return item.target === "doctor" ? "/clinic/doctor/profile/documents/upload" : `/clinic/doctor/${type}/${requestId}/attachments/upload`;
}

/**
 * Documents: the files that were on record when staff flagged the item, as the server reports them
 * (`original.attachments`). Null when the server does not send them (older API) — callers then fall
 * back to what the page saw first.
 */
export function originalFiles(item: Pick<RoundItem, "original">): RoundAttachment[] | null {
  const o = item.original;
  if (!o || typeof o !== "object" || !Array.isArray(o.attachments)) return null;
  return o.attachments.filter((a) => !!a && !!a.id);
}

/** The document type to send with an upload: exactly the flagged one. */
export const uploadDocumentType = (item: RoundItem) => item.document_type || item.field_name;

// ---------------------------------------------------------------- kinds & grouping

export function kindOf(item: Pick<RoundItem, "kind" | "field_type" | "field_name">): CorrKind {
  if (item.kind === "document" || item.kind === "location" || item.kind === "field") return item.kind;
  if (item.field_type === "document") return "document";
  if (item.field_name === "location_coordinates") return "location";
  return "field";
}

export const TARGET_ORDER: CorrTarget[] = ["doctor", "request", "clinic"];

export function targetOf(item: Pick<RoundItem, "target">): CorrTarget {
  return item.target === "doctor" || item.target === "clinic" ? item.target : "request";
}

export interface ItemGroup {
  target: CorrTarget;
  items: RoundItem[];
}

/** Items grouped «ملفك الشخصي» → «هذا الطلب» → «العيادة», keeping the server's order inside a group. */
export function groupItems(round: Pick<DoctorRound, "items" | "groups">): ItemGroup[] {
  const byId = new Map(round.items.map((i) => [i.id, i]));
  const placed = new Set<string>();
  const buckets: Record<CorrTarget, RoundItem[]> = { doctor: [], request: [], clinic: [] };
  (round.groups || []).forEach((g) => {
    const target: CorrTarget = g.target === "doctor" || g.target === "clinic" ? g.target : "request";
    g.item_ids.forEach((id) => {
      const it = byId.get(id);
      if (it && !placed.has(id)) {
        buckets[target].push(it);
        placed.add(id);
      }
    });
  });
  round.items.forEach((it) => {
    if (!placed.has(it.id)) {
      buckets[targetOf(it)].push(it);
      placed.add(it.id);
    }
  });
  return TARGET_ORDER.filter((t) => buckets[t].length).map((t) => ({ target: t, items: buckets[t] }));
}

// ---------------------------------------------------------------- server-confirmed progress

/** An item counts as done only when the server says so (saved answer / fresh upload / exempt). */
export function isConfirmed(item: RoundItem): boolean {
  return !!item.addressed || item.doctor_writable === false;
}

export interface Progress {
  total: number;
  done: number;
  remaining: number;
  pct: number;
  missingIds: string[];
}

export function serverProgress(round: Pick<DoctorRound, "items">): Progress {
  const total = round.items.length;
  const missingIds = round.items.filter((i) => !isConfirmed(i)).map((i) => i.id);
  const done = total - missingIds.length;
  return { total, done, remaining: missingIds.length, pct: total ? Math.round((done / total) * 100) : 0, missingIds };
}

/** Same rule on the /progress mirror (home card): saved items + exempt items. */
export function progressMirrorDone(items: Array<{ response_status?: string | null; doctor_writable?: boolean | null }>): number {
  return items.filter((i) => i.response_status === "saved" || i.response_status === "resubmitted" || i.doctor_writable === false).length;
}

// ---------------------------------------------------------------- values

const isGeo = (v: any): v is GeoAnswer => !!v && typeof v === "object" && "lat" in v && "lng" in v;

/** Canonical text of an answer for comparisons (trimmed, dates as YYYY-MM-DD, coordinates to 6 dp). */
export function canonical(value: any, fieldType?: string | null): string {
  if (value === null || value === undefined) return "";
  if (isGeo(value)) {
    const lat = Number(value.lat);
    const lng = Number(value.lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return "";
    return `${lat.toFixed(6)},${lng.toFixed(6)}`;
  }
  if (typeof value === "object") {
    if ("latitude" in value || "longitude" in value) {
      if (value.latitude === null || value.latitude === undefined || value.longitude === null || value.longitude === undefined) return "";
      return canonical({ lat: String(value.latitude), lng: String(value.longitude) });
    }
    if ("value" in value) return canonical(value.value, fieldType);
    return JSON.stringify(value);
  }
  let s = String(value).trim();
  if (fieldType === "date") s = s.slice(0, 10);
  if (fieldType === "number" && s !== "" && Number.isFinite(Number(s))) s = String(Number(s));
  return s;
}

/** The server-saved answer in editor form (null when nothing is saved). */
export function savedAnswer(item: RoundItem): LocalValue {
  const sv = item.saved_value;
  if (!sv) return null;
  if (kindOf(item) === "location") {
    if (sv.latitude === null || sv.latitude === undefined || sv.longitude === null || sv.longitude === undefined) return null;
    return { lat: String(sv.latitude), lng: String(sv.longitude) };
  }
  if (sv.value === null || sv.value === undefined || sv.value === "") return null;
  return typeof sv.value === "number" ? sv.value : String(sv.value);
}

/** The value on record right now (what staff flagged) in editor form. */
export function currentAnswer(item: RoundItem): LocalValue {
  const cur = item.current;
  if (!cur) return null;
  if (kindOf(item) === "location") {
    if (cur.latitude === null || cur.latitude === undefined || cur.longitude === null || cur.longitude === undefined) return null;
    return { lat: String(cur.latitude), lng: String(cur.longitude) };
  }
  if (cur.value === null || cur.value === undefined || cur.value === "") return null;
  if (item.field_type === "date") return String(cur.value).slice(0, 10);
  return typeof cur.value === "number" ? cur.value : String(cur.value);
}

/** What the editor starts with: the saved answer, else the value on record (easy to fix a typo). */
export function initialAnswer(item: RoundItem): LocalValue {
  const s = savedAnswer(item);
  return s !== null ? s : currentAnswer(item);
}

export const sameAnswer = (a: any, b: any, fieldType?: string | null) => canonical(a, fieldType) === canonical(b, fieldType);

/** The new answer matches what is on record — worth a hint, not a block (staff may accept it). */
export function unchangedFromCurrent(item: RoundItem, value: LocalValue): boolean {
  const cur = currentAnswer(item);
  return cur !== null && sameAnswer(value, cur, item.field_type);
}

// ---------------------------------------------------------------- validation

export type AnswerProblem = "required" | "number" | "date" | "location" | "too_long" | "email" | null;

const MAX_TEXT = 4000;

/** An e-mail item (the server validates it as an address → 422 corrections.invalid_value). */
export const isEmailItem = (item: Pick<RoundItem, "field_name">) => /(^|_)email$/i.test(item.field_name || "");

/** Loose client-side shape check (the server has the final word). */
export const looksLikeEmail = (s: string) => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(s.trim());

export function validateAnswer(item: RoundItem, value: LocalValue): AnswerProblem {
  const kind = kindOf(item);
  if (kind === "document") return null;
  if (kind === "location") {
    if (!isGeo(value)) return "required";
    const lat = Number(value.lat);
    const lng = Number(value.lng);
    if (String(value.lat).trim() === "" || String(value.lng).trim() === "") return "required";
    if (!Number.isFinite(lat) || !Number.isFinite(lng) || Math.abs(lat) > 90 || Math.abs(lng) > 180) return "location";
    return null;
  }
  const s = value === null || value === undefined ? "" : String(value).trim();
  if (!s) return "required";
  if (item.field_type === "number" && !/^\d+$/.test(s)) return "number";
  if (item.field_type === "date") {
    if (!/^\d{4}-\d{2}-\d{2}/.test(s) || Number.isNaN(new Date(s.slice(0, 10)).getTime())) return "date";
  }
  if (s.length > MAX_TEXT) return "too_long";
  if (isEmailItem(item) && !looksLikeEmail(s)) return "email";
  return null;
}

export function problemText(p: AnswerProblem, lang: Lang): string {
  const m: Record<Exclude<AnswerProblem, null>, [string, string]> = {
    required: ["هذا البند مطلوب", "This item is required"],
    number: ["أدخل أرقاماً فقط", "Enter digits only"],
    date: ["أدخل تاريخاً صحيحاً", "Enter a valid date"],
    location: ["اختر موقعاً صحيحاً على الخريطة", "Choose a valid location on the map"],
    too_long: ["النص أطول من المسموح", "The text is too long"],
    email: ["أدخل بريداً إلكترونياً صحيحاً، مثل name@example.com", "Enter a valid email address, like name@example.com"],
  };
  if (!p) return "";
  return lang === "en" ? m[p][1] : m[p][0];
}

// ---------------------------------------------------------------- payloads

export type SaveBody = { round_id: string; version: number; value: any } | { round_id: string; version: number; latitude: number | null; longitude: number | null };

/** PUT body for one field/location item (null clears). Documents are saved by upload → null. */
export function buildSaveBody(round: Pick<DoctorRound, "round_id" | "version">, item: RoundItem, value: LocalValue): SaveBody | null {
  const kind = kindOf(item);
  if (kind === "document") return null;
  if (kind === "location") {
    if (!isGeo(value)) return { round_id: round.round_id, version: round.version, latitude: null, longitude: null };
    return { round_id: round.round_id, version: round.version, latitude: Number(value.lat), longitude: Number(value.lng) };
  }
  let v: any = value === undefined ? null : value;
  if (typeof v === "string") v = v.trim();
  if (v === "") v = null;
  if (v !== null && item.field_type === "number") v = Number(v);
  if (v !== null && item.field_type === "date") v = String(v).slice(0, 10);
  return { round_id: round.round_id, version: round.version, value: v };
}

/** POST resubmit body: only the round's own ids; answers were saved item by item beforehand. */
export function buildResubmitBody(round: Pick<DoctorRound, "round_id" | "version">): { round_id: string; version: number; values: Record<string, any> } {
  return { round_id: round.round_id, version: round.version, values: {} };
}

/**
 * Legacy flat resubmit body (POST /clinic/doctor/{type}/{id}/resubmit) — classic screen fallback.
 * Only flagged keys: field items by field_name, a location item as latitude/longitude
 * (clinic_latitude/clinic_longitude for closures and clinic-target items); documents add nothing.
 */
export function buildLegacyResubmitBody(
  items: Array<{ field_name: string; field_type?: string | null; kind?: string | null; target?: string | null; item_target?: string | null }>,
  values: Record<string, any>,
  requestType: string
): Record<string, any> {
  const out: Record<string, any> = {};
  items.forEach((it) => {
    const kind = kindOf({ kind: it.kind || undefined, field_type: it.field_type, field_name: it.field_name } as RoundItem);
    if (kind === "document") return;
    if (kind === "location") {
      const g = values[it.field_name] ?? values.location_coordinates;
      if (!isGeo(g)) return;
      const clinicSide = requestType === "closure" || it.item_target === "clinic";
      out[clinicSide ? "clinic_latitude" : "latitude"] = Number(g.lat);
      out[clinicSide ? "clinic_longitude" : "longitude"] = Number(g.lng);
      return;
    }
    let v = values[it.field_name];
    if (v === undefined || v === null || String(v).trim() === "") return;
    if (it.field_type === "number") v = Number(v);
    out[it.field_name] = v;
  });
  return out;
}

// ---------------------------------------------------------------- errors

export type CorrErrorKind =
  | "stale" // the round changed or closed meanwhile → reload and explain
  | "not_flagged" // uploaded/sent something that is not in the round → reload
  | "incomplete" // resubmit before every item was answered
  | "invalid" // a value failed validation (per item)
  | "not_editable" // the request is no longer waiting for corrections
  | "session" // signed out / session expired
  | "network"
  | "aborted"
  | "other";

export interface CorrError {
  kind: CorrErrorKind;
  /** item id → message (invalid) */
  itemErrors: Record<string, string>;
  /** items the server still needs (incomplete) */
  missingIds: string[];
  error: ApiError;
}

export function classifyError(err: ApiError): CorrError {
  const code = err.message_code || "";
  const details = err.details || {};
  const base: CorrError = { kind: "other", itemErrors: {}, missingIds: [], error: err };
  if (code === "aborted") return { ...base, kind: "aborted" };
  if (err.status === 401 || err.status === 444 || code === "auth.session_expired") return { ...base, kind: "session" };
  if (err.status === 0 || code === "network.error") return { ...base, kind: "network" };
  if (code === "corrections.document_not_flagged" || code === "corrections.field_not_flagged" || code === "corrections.item_not_in_round" || code === "corrections.item_saved_by_upload")
    return { ...base, kind: "not_flagged" };
  if (code === "corrections.items_incomplete") {
    const ids = Array.isArray(details.items) ? details.items.map((x: any) => String(x?.id || "")).filter(Boolean) : [];
    return { ...base, kind: "incomplete", missingIds: ids };
  }
  if (code === "corrections.invalid_value" || (err.status === 422 && Array.isArray(details.errors))) {
    const itemErrors: Record<string, string> = {};
    (Array.isArray(details.errors) ? details.errors : []).forEach((e: any) => {
      if (e && e.item_id) itemErrors[String(e.item_id)] = String(e.message || "");
    });
    return { ...base, kind: "invalid", itemErrors };
  }
  if (code === "corrections.invalid_status" || code === "clinic.request.not_editable" || code === "corrections.not_found") return { ...base, kind: "not_editable" };
  if (err.status === 409 || code === "corrections.round_stale" || code === "corrections.version_conflict") return { ...base, kind: "stale" };
  return base;
}

/**
 * Words for a server validation message (422 corrections.invalid_value). The server's per-field text is
 * a technical English message, so the doctor gets a plain localized sentence instead.
 */
export function invalidValueText(serverMessage: string | null | undefined, lang: Lang): string {
  const m = String(serverMessage || "");
  const en = lang === "en";
  const min = m.match(/greater than or equal to (\d+)/i) || m.match(/greater than (\d+)/i);
  if (min) return en ? `The value is too small (the lowest accepted is ${min[1]}).` : `القيمة أصغر من المسموح (أقل قيمة مقبولة ${min[1]}).`;
  const max = m.match(/less than or equal to (\d+)/i) || m.match(/less than (\d+)/i);
  if (max) return en ? `The value is too large (the highest accepted is ${max[1]}).` : `القيمة أكبر من المسموح (أعلى قيمة مقبولة ${max[1]}).`;
  if (/email/i.test(m)) return en ? "Enter a valid email address, like name@example.com." : "أدخل بريداً إلكترونياً صحيحاً، مثل name@example.com.";
  if (/integer|number|float|decimal|digit/i.test(m)) return en ? "Enter a number (digits only)." : "أدخل رقماً (أرقام فقط).";
  if (/date|time/i.test(m)) return en ? "Enter a valid date." : "أدخل تاريخاً صحيحاً.";
  if (/at most|too long|max/i.test(m)) return en ? "The value is too long." : "القيمة أطول من المسموح.";
  if (/enum|permitted|one of|literal/i.test(m)) return en ? "Choose one of the listed options." : "اختر أحد الخيارات المتاحة.";
  return en ? "This value is not accepted for this item. Check it and try again." : "هذه القيمة غير مقبولة لهذا البند. تحقق منها ثم أعد المحاولة.";
}

/** Items added / removed between two loads of the round (after a 409 reload). */
export function diffRounds(prev: Pick<DoctorRound, "round_id" | "items"> | null, next: Pick<DoctorRound, "round_id" | "items"> | null): { added: string[]; removed: string[]; newRound: boolean } {
  if (!prev || !next) return { added: [], removed: [], newRound: !!prev !== !!next };
  const before = new Set(prev.items.map((i) => i.id));
  const after = new Set(next.items.map((i) => i.id));
  return {
    added: next.items.filter((i) => !before.has(i.id)).map((i) => i.id),
    removed: prev.items.filter((i) => !after.has(i.id)).map((i) => i.id),
    newRound: prev.round_id !== next.round_id,
  };
}

// ---------------------------------------------------------------- local draft (per doctor + request)

export interface CorrectionsDraft {
  round_id: string;
  /** item id → typed answer (fields: string/number; location: {lat, lng, address}) — never files. */
  values: Record<string, LocalValue>;
}

/** Keep only answers for editable field/location items of THIS round that differ from the server's saved answer. */
export function mergeDraft(round: DoctorRound, draft: CorrectionsDraft | null | undefined): { values: Record<string, LocalValue>; dropped: number } {
  const values: Record<string, LocalValue> = {};
  let dropped = 0;
  if (!draft || !draft.values || typeof draft.values !== "object") return { values, dropped };
  const byId = new Map(round.items.map((i) => [i.id, i]));
  Object.entries(draft.values).forEach(([id, v]) => {
    const item = byId.get(id);
    if (!item || item.doctor_writable === false || kindOf(item) === "document") {
      dropped += 1;
      return;
    }
    if (kindOf(item) === "location" && v !== null && !isGeo(v)) {
      dropped += 1;
      return;
    }
    const saved = savedAnswer(item);
    if (saved !== null && sameAnswer(v, saved, item.field_type)) return; // already on the server
    values[id] = v;
  });
  return { values, dropped };
}

/**
 * Items whose local (touched) answer is not on the server yet: it differs from the saved answer,
 * or nothing is saved and the local answer is not empty.
 */
export function dirtyItemIds(round: Pick<DoctorRound, "items">, local: Record<string, LocalValue>): string[] {
  return round.items
    .filter((it) => {
      if (!(it.id in local) || it.doctor_writable === false || kindOf(it) === "document") return false;
      const v = local[it.id];
      const saved = savedAnswer(it);
      if (saved === null) return canonical(v, it.field_type) !== "";
      return !sameAnswer(v, saved, it.field_type);
    })
    .map((it) => it.id);
}

/** Draft to persist: only answers that are not on the server yet (no files). Null → remove the draft. */
export function draftToStore(round: DoctorRound, local: Record<string, LocalValue>): CorrectionsDraft | null {
  const ids = dirtyItemIds(round, local);
  if (!ids.length) return null;
  const values: Record<string, LocalValue> = {};
  ids.forEach((id) => (values[id] = local[id]));
  return { round_id: round.round_id, values };
}

// ---------------------------------------------------------------- deadline

export interface DeadlineView {
  state: DeadlineState;
  tone: "info" | "warning" | "danger" | "neutral";
  title: string;
  body: string;
}

const fmtDay = (iso: string | null | undefined, lang: Lang) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(lang === "en" ? "en-GB" : "ar-IQ-u-nu-latn", { day: "numeric", month: "long", year: "numeric" });
};

const daysText = (n: number, lang: Lang) => {
  if (lang === "en") return n === 1 ? "1 day" : `${n} days`;
  if (n === 1) return "يوم واحد";
  if (n === 2) return "يومان";
  if (n >= 3 && n <= 10) return `${n} أيام`;
  return `${n} يوماً`;
};

/** Text for the API's deadline object; null when there is nothing to show. Never assumes a day count. */
export function deadlineView(d: DeadlineInfo | null | undefined, lang: Lang): DeadlineView | null {
  if (!d || !d.state) return null;
  const en = lang === "en";
  const tr = (ar: string, e: string) => (en ? e : ar);
  const date = fmtDay(d.auto_reject_at, lang);
  const days = typeof d.days_remaining === "number" ? d.days_remaining : null;
  switch (d.state) {
    case "active":
      return {
        state: "active",
        tone: "info",
        title: days !== null ? tr(`المهلة المتبقية: ${daysText(days, lang)}`, `Time left: ${daysText(days, lang)}`) : tr("لإعادة الإرسال مهلة محددة", "Resubmission has a deadline"),
        body: date
          ? tr(`أعد الإرسال قبل ${date}. بعد هذا التاريخ قد يُرفض الطلب تلقائياً.`, `Resubmit before ${date}. After that date the request may be rejected automatically.`)
          : tr("أعد الإرسال في أقرب وقت حتى لا يُرفض الطلب تلقائياً.", "Resubmit soon so the request is not rejected automatically."),
      };
    case "warning":
      return {
        state: "warning",
        tone: "warning",
        title: days !== null ? tr(`اقتربت المهلة: تبقّى ${daysText(days, lang)}`, `Deadline close: ${daysText(days, lang)} left`) : tr("اقتربت المهلة", "The deadline is close"),
        body: date
          ? tr(`إن لم تُعد الإرسال قبل ${date} قد يُرفض الطلب تلقائياً.`, `If you do not resubmit before ${date}, the request may be rejected automatically.`)
          : tr("أعد الإرسال الآن حتى لا يُرفض الطلب تلقائياً.", "Resubmit now so the request is not rejected automatically."),
      };
    case "overdue":
      return {
        state: "overdue",
        tone: "danger",
        title: tr("انتهت المهلة", "The deadline has passed"),
        body: date
          ? tr(`انتهت مهلة إعادة الإرسال في ${date}، وقد يُرفض الطلب تلقائياً في أي وقت. أعد الإرسال الآن.`, `The resubmission deadline passed on ${date} and the request can be rejected automatically at any time. Resubmit now.`)
          : tr("قد يُرفض الطلب تلقائياً في أي وقت. أعد الإرسال الآن.", "The request can be rejected automatically at any time. Resubmit now."),
      };
    case "exhausted":
      return {
        state: "exhausted",
        tone: "warning",
        title: tr("انتهت المهلة — لن يُرفض طلبك تلقائياً", "The deadline has passed — no automatic rejection"),
        body: tr("لم تصلك تذكيراتنا، لذلك سيتواصل معك موظف النقابة قبل أي قرار. أعد الإرسال في أقرب وقت.", "Our reminders did not reach you, so association staff will contact you before any decision. Resubmit as soon as you can."),
      };
    case "not_applicable":
      return null;
    default:
      return null;
  }
}

// ---------------------------------------------------------------- labels

export function targetLabel(t: CorrTarget, lang: Lang): { title: string; desc: string } {
  const m: Record<CorrTarget, [string, string, string, string]> = {
    doctor: ["ملفك الشخصي", "بياناتك ومستنداتك الشخصية لدى النقابة", "Your profile", "Your personal details and documents held by the association"],
    request: ["هذا الطلب", "معلومات الطلب ومرفقاته", "This request", "The request's details and attachments"],
    clinic: ["العيادة", "سجل العيادة المرتبط بالطلب", "The clinic", "The clinic record this request refers to"],
  };
  const [ar, arD, e, eD] = m[t];
  return lang === "en" ? { title: e, desc: eD } : { title: ar, desc: arD };
}

/** Route of the corrections flow for one request (the type travels in the URL). */
export function correctionsRoute(req: { request_id?: string | null; request_type?: string | null }, step?: "review"): string {
  const q = new URLSearchParams();
  if (req.request_id) q.set("request", req.request_id);
  if (req.request_type) q.set("type", req.request_type);
  if (step) q.set("step", step);
  const s = q.toString();
  return s ? `/corrections?${s}` : "/corrections";
}
